import {
  W, DROPPER_Y, DROP_COOLDOWN_MS, WATERMELON_BONUS, SPAWN_TIERS, MAX_TIER,
  GamePhase, Effect, Particle, PuffBlob,
} from './types';
import { fruitRadius, fruitDef, preloadSprites } from './fruits';
import {
  initPhysics, spawnFruit, step, resolveMerges, findOverlapMerges, checkGameOver, reset, shiftSpawnTimes,
  MergeResult,
} from './physics';
import { setupCanvas, renderFrame, Scene, getGold } from './renderer';
import { attachInput } from './input';
import { getBest, setBest } from './storage';
import { track } from './analytics';

const STEP_MS = 1000 / 60;
const SWEEP_MS = 800;   // safety-net overlap check for stuck same-tier pairs

let phase: GamePhase = 'MENU';
let score = 0;
let best = getBest();
let dropperX = W / 2;
let currentTier = 1;
let nextTier = 1;
let lastDropMs = -Infinity;
let overlayStartMs = 0;
let shakeStartMs = -Infinity;
let pauseStartMs = 0; // performance.now() at the moment PLAYING->PAUSED, for grace-period shift on resume
let effects: Effect[] = [];

let ctx: CanvasRenderingContext2D;

function randTier(): number {
  return SPAWN_TIERS[(Math.random() * SPAWN_TIERS.length) | 0];
}

function startGame(): void {
  reset();
  score = 0;
  currentTier = randTier();
  nextTier = randTier();
  dropperX = W / 2;
  lastDropMs = -Infinity;
  effects = [];
  shakeStartMs = -Infinity;
  phase = 'PLAYING';
  track('game_start');
}

function doDrop(now: number): void {
  if (phase !== 'PLAYING' || now - lastDropMs < DROP_COOLDOWN_MS) return;
  spawnFruit(currentTier, dropperX, DROPPER_Y, now, 0.7);
  currentTier = nextTier;
  nextTier = randTier();
  lastDropMs = now;
}

function spawnPuff(baseR: number): PuffBlob[] {
  const blobs: PuffBlob[] = [];
  const n = 5;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + Math.random() * 0.6;
    const d = baseR * (0.3 + Math.random() * 0.3);
    blobs.push({ dx: Math.cos(a) * d, dy: Math.sin(a) * d, r: baseR * (0.28 + Math.random() * 0.18) });
  }
  return blobs;
}

function spawnParticles(x: number, y: number, color: string, n: number): Particle[] {
  const parts: Particle[] = [];
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = 0.08 + Math.random() * 0.12;
    parts.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 0.05, color, size: 3 + Math.random() * 3 });
  }
  return parts;
}

function handleMerges(results: MergeResult[], now: number): void {
  for (const m of results) {
    if (m.doubleWatermelon) {
      score += WATERMELON_BONUS;
      effects.push({ kind: 'gold', x: m.x, y: m.y, r: fruitRadius(MAX_TIER), startMs: now, dur: 400 });
      effects.push({ kind: 'particles', parts: spawnParticles(m.x, m.y, getGold(), 16), startMs: now, dur: 700 });
      effects.push({ kind: 'popup', x: m.x, y: m.y, text: '+' + WATERMELON_BONUS, startMs: now, dur: 900 });
      shakeStartMs = now;
      continue;
    }
    score += fruitDef(m.newTier).points;
    effects.push({ kind: 'flash', x: m.x, y: m.y, r: fruitRadius(m.newTier) * 1.3, startMs: now, dur: 150 });
    effects.push({ kind: 'puff', x: m.x, y: m.y, blobs: spawnPuff(fruitRadius(m.newTier)), startMs: now, dur: 280 });
    if (m.newTier === MAX_TIER) {
      effects.push({ kind: 'gold', x: m.x, y: m.y, r: fruitRadius(MAX_TIER), startMs: now, dur: 400 });
      effects.push({ kind: 'particles', parts: spawnParticles(m.x, m.y, getGold(), 14), startMs: now, dur: 600 });
    }
  }
}

function gameOver(now: number): void {
  const isRecord = score > best;
  best = setBest(score);
  if (isRecord) track('new_best', { score });
  track('game_over', { result: 'lose', score });
  overlayStartMs = now;
  phase = 'GAME_OVER';
}

function togglePause(): void {
  if (phase === 'PLAYING') {
    phase = 'PAUSED';
    pauseStartMs = performance.now();
  } else if (phase === 'PAUSED') {
    phase = 'PLAYING';
    shiftSpawnTimes(performance.now() - pauseStartMs);
  }
}

function pressButton(name: string): void {
  switch (name) {
    case 'play': case 'again': case 'restart': startGame(); break;
    case 'pause': case 'resume': togglePause(); break;
    case 'menu': phase = 'MENU'; break;
    case 'home': location.href = document.body.dataset.hubUrl ?? '/'; break;
  }
}

function shakeOffset(now: number): { x: number; y: number } {
  const t = (now - shakeStartMs) / 300;
  if (t < 0 || t > 1) return { x: 0, y: 0 };
  const amp = 5 * (1 - t);
  return { x: Math.sin(now / 18) * amp, y: Math.cos(now / 23) * amp };
}

let lastTime = 0;
let acc = 0;
let sweepAcc = 0;

function frame(now: number): void {
  try {
    const dt = lastTime ? Math.min(now - lastTime, 100) : 0;
    lastTime = now;

    if (phase === 'PLAYING') {
      acc += dt;
      while (acc >= STEP_MS) {
        step(STEP_MS);
        acc -= STEP_MS;
      }
      sweepAcc += dt;
      if (sweepAcc >= SWEEP_MS) {
        sweepAcc = 0;
        findOverlapMerges();   // catch touching same-tier pairs missed by collision events
      }
      handleMerges(resolveMerges(now), now);
      if (checkGameOver(now)) gameOver(now);
    } else {
      acc = 0;
    }

    effects = effects.filter((e) => now - e.startMs < e.dur);
    const sh = shakeOffset(now);

    const scene: Scene = {
      phase, score, best, dropperX, currentTier, nextTier, effects,
      shakeX: sh.x, shakeY: sh.y, overlayStartMs, now,
    };
    renderFrame(ctx, scene);
  } catch (err) {
    console.error(err);
  }
  requestAnimationFrame(frame);
}

async function init(): Promise<void> {
  const canvas = document.getElementById('game') as HTMLCanvasElement;
  ctx = setupCanvas(canvas);
  initPhysics();

  await preloadSprites();

  attachInput(canvas, {
    getPhase: () => phase,
    getCurrentRadius: () => fruitRadius(currentTier),
    getDropperX: () => dropperX,
    moveDropper: (x) => { dropperX = x; },
    drop: () => doDrop(performance.now()),
    pressButton,
    togglePause,
    restart: startGame,
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden && phase === 'PLAYING') {
      phase = 'PAUSED';
      pauseStartMs = performance.now();
    }
  });

  requestAnimationFrame(frame);
}

init();
